import { defaultCompareFn, sortAdapter } from "@/pages/dashboard/components/CommonTable/utils";
import { IColumn, ICommonTableProps } from "@/pages/dashboard/components/CommonTable/type";
import { IRankingItem, ITableRow, TPlatform } from "@/pages/dashboard/pages/changeAnalysis/components/RankingChangeTable/types";
import { transformDownloadData, transformRankingData } from "@/pages/dashboard/pages/changeAnalysis/components/RankingChangeTable/utils";
import { useLocal } from "@/pages/dashboard/utils/customHook";
import { SortItem } from "ali-react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";

export const useRankingChangeTableState = ({
  defaultSegment = 'appStore',
  pagination,
}: {
  defaultSegment?: TPlatform;
  pagination?: NonNullable<ICommonTableProps['commonBaseTableProps']['tableConfig']>['pagination'];
}) => {
  const { pageSize: lPageSize = 10, pageIndex: lPageIndex = 1 } = pagination || {};
  const [platform, setPlatform] = useState<TPlatform>(defaultSegment);
  const [pageSize, setPageSize] = useState(lPageSize);
  const [pageIndex, setPageIndex] = useState(lPageIndex);
  const [sortedData, setSortedData] = useState<ITableRow[]>([]);

  const handlePlatformChange = (value: TPlatform) => {
    setPlatform(value);
    setPageIndex(1);
  };

  return {
    platform,
    setPlatform,
    pageSize,
    setPageSize,
    pageIndex,
    setPageIndex,
    sortedData,
    setSortedData,
    handlePlatformChange,
  };
};

export const useRankingChangeTableEffects = ({
  changeData,
  platform,
  columns,
  metricsCardWrapProps,
  transformedData,
}: {
  changeData: (key: string, value: unknown) => void;
  platform: TPlatform;
  columns: IColumn[];
  metricsCardWrapProps: ICommonTableProps['metricsCardWrapProps'];
  transformedData: ITableRow[];
}) => {
  const { isEN } = useLocal();
  const isInitialPlatformSync = useRef(true);

  useEffect(() => {
    // 首次 mount 跳过：Service config 中已有正确的初始 rankingChangeTable，
    // 避免创建新对象引用触发 Service useEffect 的二次请求
    if (isInitialPlatformSync.current) {
      isInitialPlatformSync.current = false;
      return;
    }
    changeData('rankingChangeTable', {
      platform,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  useEffect(() => {
    if (!changeData) {
      return;
    }

    const games = columns
      ?.filter((col: IColumn) => col.code && col.code !== 'country')
      .map((col: IColumn) => ({
        name: col.code,
        gameId: col.code,
        icon: '',
      })) || [];

    const downloadData = transformDownloadData(
      transformedData,
      games,
      platform,
      isEN
    );

    changeData('__registerTable__', {
      id: 'rankingChangeTable',
      titleEn: metricsCardWrapProps?.titleEn || '排行榜变动',
      titleCn: metricsCardWrapProps?.titleCn || '排行榜变动',
      columns: downloadData.columns,
      dataSource: downloadData.data,
      headerRows: downloadData.headerRows,
      mergeCells: downloadData.mergeCells,
    });

    return () => {
      changeData('__unregisterTable__', 'rankingChangeTable');
    };
  }, [isEN, platform, transformedData]);
};

export const useRankingChangeTableData = (data: IRankingItem[], platform: TPlatform, gameNameMap: Record<string, string>, platformDimMap: Record<TPlatform, string[]>) => {
  const emptyRef = useRef<IRankingItem[]>([]);

  const safeData = useMemo(() => {
    return Array.isArray(data) && data.length > 0 ? data : emptyRef.current;
  }, [data]);

  return useMemo(() => {
    /** 测试 */
    // const mockData = generateAllData(platformDimMap);
    // const result = transformRankingData(mockData, platform, gameNameMap, platformDimMap);
    const result = transformRankingData(safeData, platform, gameNameMap, platformDimMap);
    return result;
  }, [safeData, platform]);
};

export const handleSortChange = (
  transformedData: ITableRow[],
  setSortedData: React.Dispatch<React.SetStateAction<ITableRow[]>>
) => (
  sorts: SortItem[]
) => {
  if (!sorts || sorts.length === 0) {
    setSortedData([]);
    return;
  }

  const aliSorts = sortAdapter.toPopover(sorts);

  const sortedData = [...transformedData].sort((a, b) => {
    for (const sort of aliSorts) {
      const { by, order } = sort;
      const desc = order === 'desc';
      const cmp = defaultCompareFn(a[by], b[by], desc);
      if (cmp !== 0) {
        return desc ? -cmp : cmp;
      }
    }
    return 0;
  });

  setSortedData(sortedData);
};
